/**
 * On-device persistence for Characters and app settings, in IndexedDB.
 *
 * Stores:
 *   characters  full Character records, keyed by id
 *   summaries   one CharacterSummary per Character, keyed by id, for the home screen
 *   settings    app settings, keyed by name
 *   bookmarks   Entries the user marked to find again (#15), keyed by uuid
 *   recents     Entries the user viewed lately (#15), keyed by uuid, capped at RECENTS_CAP
 *   sync        what the sync engine (#25) knows about each record, keyed "<collection>:<id>"
 *
 * Every write to `characters` updates `summaries` in the same transaction, so the two never
 * disagree. Reads for the Character list touch only `summaries` (issue #4 acceptance).
 *
 * Sync bookkeeping lives in its own store and never on a Character, so a Character's schema and
 * its exported file are the same whether the Device is signed in or not (#25).
 */
import type { SyncSession } from "../sync/model";
import type { Character, LevelNumber } from "./model";
import { countUnfilled, foundation } from "./model";
import { migrateCharacter } from "./migrate";
import { recordChanged } from "./records";
import { validateCharacter } from "./validate";

export const DB_NAME = "pathfinder-offline";
/** Bump together with a new entry in MIGRATIONS. */
export const DB_VERSION = 4;

export const STORE_CHARACTERS = "characters";
export const STORE_SUMMARIES = "summaries";
export const STORE_SETTINGS = "settings";
export const STORE_BOOKMARKS = "bookmarks";
export const STORE_RECENTS = "recents";
export const STORE_SYNC = "sync";

export interface CharacterSummary {
  id: string;
  name: string;
  level: LevelNumber;
  snapshotTag: string;
  updatedAt: string;
  /** Level 1 foundations by UUID, when chosen; the list resolves names through the catalogue. */
  ancestryUuid?: string;
  classUuid?: string;
  /** Whether a Session is open on the Character (updates must not apply under one, docs/design.md). */
  sessionOpen: boolean;
  /** Choices up to the Character's Level with no Selection or answer (#9). */
  openChoices: number;
}

export function summarize(c: Character): CharacterSummary {
  const s: CharacterSummary = {
    id: c.id,
    name: c.name,
    level: c.level,
    snapshotTag: c.snapshotTag,
    updatedAt: c.updatedAt,
    sessionOpen: c.session !== null,
    openChoices: countUnfilled(c),
  };
  const ancestry = foundation(c, "ancestry")?.uuid;
  const cls = foundation(c, "class")?.uuid;
  if (ancestry) s.ancestryUuid = ancestry;
  if (cls) s.classUuid = cls;
  return s;
}

type Migration = (db: IDBDatabase, tx: IDBTransaction) => void;

/**
 * Index n lifts database version n-1 to n. Each step sees the stores the earlier steps created
 * and may read and write data through `tx`.
 */
const MIGRATIONS: Record<number, Migration> = {
  1: (db) => {
    db.createObjectStore(STORE_CHARACTERS, { keyPath: "id" });
    db.createObjectStore(STORE_SETTINGS, { keyPath: "key" });
  },
  2: (db, tx) => {
    const summaries = db.createObjectStore(STORE_SUMMARIES, { keyPath: "id" });
    summaries.createIndex("updatedAt", "updatedAt");
    // Backfill from the Characters a version 1 database already holds.
    const cursor = tx.objectStore(STORE_CHARACTERS).openCursor();
    cursor.onsuccess = () => {
      const c = cursor.result;
      if (!c) return;
      try {
        summaries.put(summarize(validateCharacter(migrateCharacter(c.value))));
      } catch {
        // A record this build cannot read stays in `characters` and is reported when opened.
      }
      c.continue();
    };
  },
  3: (db) => {
    db.createObjectStore(STORE_BOOKMARKS, { keyPath: "uuid" }).createIndex("addedAt", "addedAt");
    db.createObjectStore(STORE_RECENTS, { keyPath: "uuid" }).createIndex("viewedAt", "viewedAt");
  },
  // Sync (#25). Empty on a Device that has never signed in, and emptied again when one signs out.
  4: (db) => {
    db.createObjectStore(STORE_SYNC, { keyPath: "key" }).createIndex("collection", "collection");
  },
};

/** Run every migration from `oldVersion` (exclusive) to `newVersion` (inclusive). */
export function upgradeDatabase(db: IDBDatabase, oldVersion: number, newVersion: number, tx: IDBTransaction): void {
  for (let v = oldVersion + 1; v <= newVersion; v++) {
    const step = MIGRATIONS[v];
    if (!step) throw new Error(`no migration to database version ${v}`);
    step(db, tx);
  }
}

let connection: Promise<IDBDatabase> | undefined;

function factory(): IDBFactory {
  const f = globalThis.indexedDB;
  if (!f) throw new Error("IndexedDB is not available");
  return f;
}

export function openDatabase(): Promise<IDBDatabase> {
  connection ??= new Promise<IDBDatabase>((resolve, reject) => {
    const req = factory().open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (ev) => {
      const tx = req.transaction;
      if (!tx) throw new Error("upgrade without a transaction");
      upgradeDatabase(req.result, ev.oldVersion, ev.newVersion ?? DB_VERSION, tx);
    };
    req.onblocked = () => reject(new Error("the database is open in another tab at an older version"));
    req.onerror = () => reject(req.error ?? new Error("could not open the database"));
    req.onsuccess = () => {
      const db = req.result;
      db.onversionchange = () => {
        db.close();
        connection = undefined;
      };
      resolve(db);
    };
  }).catch((e: unknown) => {
    connection = undefined;
    throw e;
  });
  return connection;
}

/** Close the cached connection. Tests call this between cases; the app never needs to. */
export async function closeDatabase(): Promise<void> {
  const c = connection;
  connection = undefined;
  if (c) (await c.catch(() => undefined))?.close();
}

export function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB request failed"));
  });
}

export function complete(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("transaction failed"));
    tx.onabort = () => reject(tx.error ?? new Error("transaction aborted"));
  });
}

export async function transaction(stores: string[], mode: IDBTransactionMode): Promise<IDBTransaction> {
  return (await openDatabase()).transaction(stores, mode);
}

/** Every Character's summary, most recently updated first. Reads only the summary store. */
export async function listCharacters(): Promise<CharacterSummary[]> {
  const tx = await transaction([STORE_SUMMARIES], "readonly");
  const rows = await request(tx.objectStore(STORE_SUMMARIES).index("updatedAt").getAll());
  return (rows as CharacterSummary[]).reverse();
}

/** One Character, migrated and validated. Undefined when no Character has that id. */
export async function getCharacter(id: string): Promise<Character | undefined> {
  const tx = await transaction([STORE_CHARACTERS], "readonly");
  const raw: unknown = await request(tx.objectStore(STORE_CHARACTERS).get(id));
  if (raw === undefined) return undefined;
  return validateCharacter(migrateCharacter(raw));
}

/** Save a Character and its summary atomically. Stamps `updatedAt`. Returns the saved record. */
export async function putCharacter(character: Character, at = new Date().toISOString()): Promise<Character> {
  const saved: Character = { ...character, updatedAt: at };
  const tx = await transaction([STORE_CHARACTERS, STORE_SUMMARIES], "readwrite");
  tx.objectStore(STORE_CHARACTERS).put(saved);
  tx.objectStore(STORE_SUMMARIES).put(summarize(saved));
  await complete(tx);
  recordChanged("character", saved.id, "saved");
  return saved;
}

/**
 * Remove a Character from both stores. Confirmation is the caller's job. The record as it was is
 * announced with the deletion: it is the last moment Recently Deleted (#25) can keep a copy.
 */
export async function deleteCharacter(id: string): Promise<void> {
  const tx = await transaction([STORE_CHARACTERS, STORE_SUMMARIES], "readwrite");
  const store = tx.objectStore(STORE_CHARACTERS);
  const before: unknown = await request(store.get(id));
  store.delete(id);
  tx.objectStore(STORE_SUMMARIES).delete(id);
  await complete(tx);
  recordChanged("character", id, "deleted", before);
}

export async function hasCharacter(id: string): Promise<boolean> {
  const tx = await transaction([STORE_SUMMARIES], "readonly");
  return (await request(tx.objectStore(STORE_SUMMARIES).getKey(id))) !== undefined;
}

/** App settings: small, named values (the Legacy toggle, the last opened Character, ...). */
export interface Settings {
  showLegacy: boolean;
  lastCharacterId: string | null;
  /** The colour theme: the system's, or light or dark whatever the system says (#14). */
  theme: "system" | "light" | "dark";
  /** The answer to the one persistent-storage request made when the first install completed (#3); null until then. */
  persist: { result: "granted" | "denied" | "unsupported"; at: string } | null;
  /** The Account this Device is signed in to and the Device Token it carries (#25); null while it is device-only. */
  sync: SyncSession | null;
}

export const DEFAULT_SETTINGS: Readonly<Settings> = Object.freeze({ showLegacy: false, lastCharacterId: null, theme: "system", persist: null, sync: null });

export async function getSetting<K extends keyof Settings>(key: K): Promise<Settings[K]> {
  const tx = await transaction([STORE_SETTINGS], "readonly");
  const row = (await request(tx.objectStore(STORE_SETTINGS).get(key))) as { key: K; value: Settings[K] } | undefined;
  return row ? row.value : DEFAULT_SETTINGS[key];
}

export async function setSetting<K extends keyof Settings>(key: K, value: Settings[K]): Promise<void> {
  const tx = await transaction([STORE_SETTINGS], "readwrite");
  tx.objectStore(STORE_SETTINGS).put({ key, value });
  await complete(tx);
}
